/**
 * Centralized Logging System
 * 
 * Suppresses all console output by default. Logging can only be enabled
 * via a hard-to-guess URL parameter to prevent accidental discovery.
 * 
 * To enable: Add ?kairos_debug_<hash>=1 to URL (hash is generated from secret phrase)
 */

// Secret phrase for generating hard-to-guess URL parameter
// Change this periodically for additional security
const SECRET_PHRASE = "kairos-debug-2024-secure";

// Generate hash-based parameter name
function generateParamName(): string {
  // Simple hash function (we don't need crypto-level security, just hard-to-guess)
  let hash = 0;
  for (let i = 0; i < SECRET_PHRASE.length; i++) {
    const char = SECRET_PHRASE.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to hex string and pad to 32 characters
  const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
  // Repeat pattern to get 32 chars (not cryptographically secure, but hard to guess)
  const fullHash = (hexHash + hexHash + hexHash + hexHash).substring(0, 32);
  return `kairos_debug_${fullHash}`;
}

const DEBUG_PARAM_NAME = generateParamName();
const SESSION_STORAGE_KEY = 'kairos_logging_enabled';

// Store original console methods before any overrides
// Export these so other modules (like consoleCapture) can access them
export const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
};

class Logger {
  private enabled: boolean = false;

  constructor() {
    this.checkAndEnable();
    this.overrideConsoleMethods();
  }

  /**
   * Check URL parameter and sessionStorage to determine if logging should be enabled
   */
  private checkAndEnable(): void {
    // Check sessionStorage first (persists during session)
    const sessionEnabled = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    
    if (sessionEnabled) {
      this.enabled = true;
      return;
    }

    // Check URL parameter
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const paramValue = urlParams.get(DEBUG_PARAM_NAME);
      
      if (paramValue === '1' || paramValue === 'true') {
        this.enabled = true;
        // Store in sessionStorage so it persists even if URL parameter is removed
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
        // Clean up URL parameter from address bar (optional, for cleaner URLs)
        this.cleanupUrlParameter();
      }
    }
  }

  /**
   * Remove debug parameter from URL (optional cleanup)
   */
  private cleanupUrlParameter(): void {
    if (typeof window !== 'undefined' && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete(DEBUG_PARAM_NAME);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Override all console methods to route through this logger
   */
  private overrideConsoleMethods(): void {
    // Override console.log
    console.log = (...args: any[]) => {
      if (this.enabled) {
        originalConsole.log(...args);
      }
    };

    // Override console.error
    console.error = (...args: any[]) => {
      if (this.enabled) {
        originalConsole.error(...args);
      }
    };

    // Override console.warn
    console.warn = (...args: any[]) => {
      if (this.enabled) {
        originalConsole.warn(...args);
      }
    };

    // Override console.info
    console.info = (...args: any[]) => {
      if (this.enabled) {
        originalConsole.info(...args);
      }
    };

    // Override console.debug
    console.debug = (...args: any[]) => {
      if (this.enabled) {
        originalConsole.debug(...args);
      }
    };
  }

  /**
   * Public logging methods (for explicit use in code)
   */
  log(...args: any[]): void {
    if (this.enabled) {
      originalConsole.log(...args);
    }
  }

  error(...args: any[]): void {
    if (this.enabled) {
      originalConsole.error(...args);
    }
  }

  warn(...args: any[]): void {
    if (this.enabled) {
      originalConsole.warn(...args);
    }
  }

  info(...args: any[]): void {
    if (this.enabled) {
      originalConsole.info(...args);
    }
  }

  debug(...args: any[]): void {
    if (this.enabled) {
      originalConsole.debug(...args);
    }
  }

  /**
   * Check if logging is currently enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Manually enable logging (for programmatic control)
   */
  enable(): void {
    this.enabled = true;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  }

  /**
   * Manually disable logging
   */
  disable(): void {
    this.enabled = false;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }

  /**
   * Get the debug parameter name (for documentation/debugging purposes)
   * Only available when logging is enabled
   */
  getDebugParamName(): string | null {
    return this.enabled ? DEBUG_PARAM_NAME : null;
  }
}

// Create singleton instance
let loggerInstance: Logger | null = null;

/**
 * Initialize the logger (must be called before any other code runs)
 */
export function initLogger(): Logger {
  if (!loggerInstance) {
    loggerInstance = new Logger();
  }
  return loggerInstance;
}

/**
 * Get the logger instance
 */
export function getLogger(): Logger {
  if (!loggerInstance) {
    loggerInstance = new Logger();
  }
  return loggerInstance;
}

// Export convenience methods
export const logger = {
  log: (...args: any[]) => getLogger().log(...args),
  error: (...args: any[]) => getLogger().error(...args),
  warn: (...args: any[]) => getLogger().warn(...args),
  info: (...args: any[]) => getLogger().info(...args),
  debug: (...args: any[]) => getLogger().debug(...args),
  isEnabled: () => getLogger().isEnabled(),
  enable: () => getLogger().enable(),
  disable: () => getLogger().disable(),
};

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
  initLogger();
}

